import { plumeMainnetId, ROYCO_API_URL } from '@/constants/shared';
import { RoycoTokenPosition } from '@/types';
import { isTruthy, trimSlashes } from '@/utils';
import { BaseEnrichedTokenData, RecipePositionResponse, TokenQuoteResponse } from 'royco/api';
export class RoycoClient {
  baseUrl: string;
  constructor(domain?: string) {
    if (!domain && !ROYCO_API_URL) console.warn('WARNING, NO DOMAIN SPECIFIED FOR ROYCO CLIENT. REQUESTS WILL NOT RUN');
    this.baseUrl = domain ?? ROYCO_API_URL;
  }
  // TODO use generic types instead of <any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async _fetch(input: string | URL | globalThis.Request, init?: RequestInit): Promise<any> {
    try {
      const res = await fetch(input, {
        headers: { 'x-api-key': import.meta.env.VITE_ROYCO_API_KEY },
        ...init,
      });
      return res.json();
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async get(endpoint: string, params?: { [k: string]: string | boolean }) {
    const url = new URL(trimSlashes(this.baseUrl) + '/' + trimSlashes(endpoint));
    if (params) {
      Object.keys(params).forEach((key) => url.searchParams.append(key, String(params[key])));
    }
    return this._fetch(url.toString());
  }

  async post(endpoint: string, body?: object) {
    const serializedBody = JSON.stringify(body);
    const res = await this._fetch(trimSlashes(this.baseUrl) + '/' + trimSlashes(endpoint), {
      body: serializedBody,
      method: 'POST',
    });
    return res;
  }

  /**
   *
   * Given a user address, marketId, and chainId
   * Returns all active (unclaimed, unforfeited, unwithdrawn) positions
   * As well as all aggregate values.
   */
  async getActivePositionsForUserInMarket({
    address,
    marketId,
    chainId,
  }: {
    address: string;
    marketId: string;
    chainId: string;
  }): Promise<{
    incentiveTokens: RoycoTokenPosition[];
    inputToken: RoycoTokenPosition;
    lockedInputToken: RoycoTokenPosition[];
    claimedIncentiveTokens: RoycoTokenPosition[];
    unclaimedIncentiveTokens: RoycoTokenPosition[];
  }> {
    return this.post(`/position/recipe/${chainId}_0_${marketId}/${address}`);
  }

  async getAllPositionsForUser({
    address,
    marketId,
  }: {
    address: string;
    marketId?: string;
  }): Promise<RecipePositionResponse> {
    const filters = [
      { id: 'chainId', value: plumeMainnetId },
      marketId && { id: 'marketId', value: marketId },
      address && {
        id: 'accountAddress',
        value: address,
      },
    ].filter(isTruthy);
    return this.post(`/position/recipe`, {
      filters,
      sorting: [{ id: 'unlockTimestamp', desc: true }],
      page: {
        index: 1,
        size: 20,
      },
    });
  }

  async getTokenQuote({ tokenAddress }: { tokenAddress: string }): Promise<TokenQuoteResponse> {
    return this.post(`/token/quote/${plumeMainnetId}/${tokenAddress}`);
  }

  async getGlobals({ userAddress }: { userAddress: string }): Promise<GlobalPositionResponse> {
    return this.post(`/position/global/${userAddress}`);
  }
}

type GlobalPositionResponse = {
  balanceUsd: number;
  depositBalanceUsd: number;
  incentiveBalanceUsd: number;
  incentiveTokens: BaseEnrichedTokenData[];
};

export const roycoClient = new RoycoClient();
